"use strict";

import Map from "ol/Map";
import View from "ol/View";
import {register as registerProj4} from 'ol/proj/proj4';
import * as olProj from 'ol/proj';
import * as olGeom from 'ol/geom';
import SimpleGeometry from 'ol/geom/SimpleGeometry';
import {unByKey} from "ol/Observable";
import ImageLayer from "ol/layer/Image";
import VectorLayer from "ol/layer/Vector";
import TileLayer from "ol/layer/Tile";
import LayerGroup from "ol/layer/Group";
import Overlay from "ol/Overlay";
import Feature from "ol/Feature";
import WKT from "ol/format/WKT";
import GeoJSON from "ol/format/GeoJSON";
import proj4 from "proj4";
import jQuery from 'jquery';

/**
 * @classdesc
 * Openlayers-Kartenimplementierung
 *
 * Es gibt zwei Arten der Gebietsbeschränkung:
 * a) nur options.maxExtent angegeben => Kartenansicht darf Ausdehnungsrechteck nicht verlassen
 * b) options.maxExtent gesetzt und options.constrainOnlyCenter = true => Mittelpunkt der Kartenansicht darf das angegebene
 *    Ausdehnungsrchteck nicht verlassen
 *
 * @class
 * @extends gc.gis.map.GCAbstractMap
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCMapView} holder Zugehörige Kartenwrapper-Instanz
 * @param {Object} options Aufrufoptionen (siehe auch {@link gc.gis.map.GCAbstractMap})
 * @param {string} options.target HTML-ID des Openlayers-Kartenobjekts
 * @param {number[]} [options.center=[0,0]] Mittelpunkt des initialen Kartenausschnitts
 * @param {number} [options.zoom=1] Zoom-Level des  initialen Kartenausschnitts
 * @param {string} options.projection Initiale Kartenprojektion
 * @param {gc.gis.map.GCMapExtent} options.maxExtent maximales Ausdehnungsrechteck, welches die Kartenansicht beschränkt
 * @param {boolean} [options.constrainOnlyCenter=false] optionales Flag, welches eine "weiche" Einschränkung auf das maxExtent setzt,
 * 														sodass nur der Kartenmittelpunkt innerhalb des Ausdehnungsrechteck "eingesperrt"
 * 														wird.
 */
gc.gis.OLMap = function(holder, options)
{
	gc.gis.map.GCAbstractMap.call(this, holder);

    /**
	 * Flag ob Kartenaktionen animiert werden sollen (wie z.B. Pan, Zoom)
     * @protected
     * @type boolean
     */
	this.__animateMapAction = true;

	/**
	 * Proj4 bei OL registrieren (registerProj4() from ol/proj/proj4 muss immer aufgerufen werden, wenn neue CRS bei der proj4
	 * mittels proj4.defs() angemeldet werden, passiert hier aber nur statisch siehe proj4/epsg.js).
	 */
	registerProj4(proj4);

	/**
	 * Factory zur Erzeugung div. Map-Objekte.
	 * @protected
	 * @type {gc.gis.factory.OLFactory}
	 */
	this._object_factory = new gc.gis.factory.OLFactory();

	/**
	 * Factory zur Erzeugung von Styles.
	 * @protected
	 * @type {gc.gis.factory.OLFactory}
	 * @type {gc.gis.factory.OLStyleFactory}
	 */
	this._style_factory = new gc.gis.factory.OLStyleFactory();


	this._target = options.target;

	/**
	 * Maximales Ausdehnungsrechteck der Kartenansicht.
	 * @type {gc.gis.map.GCMapExtent}
	 * @private
	 */
	this._maxExtent = options.maxExtent;

	/**
	 * Projektion des maximalen Ausdehnungsrechtecks der Kartenansicht.
	 * @type {ol.proj.Projection}
	 * @private
	 */
	this._maxExtentProjection = undefined;

	/**
	 * Flag für eine weiche Einschränkung des Ausdehnungsrechtecks, sodass nur der Kartenmittelpunkt
	 * innerhalb des maxExtent eingesperrt wird.
	 * @type {boolean}
	 * @private
	 */
	this._constrainOnlyCenter = false;

	/**
	 * OL-Map
	 * @protected
	 * @type ol.Map
	 */
	this._map = undefined;

	/**
	 * OL-Interaktionen
	 * @protected
	 * @type ol.interaction.Interaction[]
	 */
	this._last_interactions = [];


	if (this._maxExtent)
	{
		this._maxExtentProjection = olProj.get(options.projection);
		this._constrainOnlyCenter = options.constrainOnlyCenter || false;
	}

	this.__initMap(options);
	this.__initLayers();

	// Interaktionen überwachen
	this.__initInteractions();

	/**
	 * @protected
	 * @type gc.gis.map.GCAbstractLayerFactory
	 */
	this._layer_factory = new gc.gis.OLLayerFactory({
		map: this.getMapView()
	});
};
gc.gis.OLMap.prototype = Object.create( gc.gis.map.GCAbstractMap.prototype );

/**
 * Initialisiert die Openlayers-Karte.
 *
 * @private
 * @param {Object} options Aufrufoptionen für die Openlayers-Karte
 * @param {string} options.target HTML-ID des Zielelements
 * @param {string} options.projection Koordinatenreferenzsystem der Karte
 * @param {number[]} [options.center=[0,0]] initialer Kartenmittelpunkt
 * @param {number} [options.zoom=1] initiales Zoomlevel
 */
gc.gis.OLMap.prototype.__initMap = function(options)
{
	const self = this;
	const center = options.center || [0,0];
	const zoom = options.zoom || 1;
	const projection = olProj.get(options.projection);

	const view = this.__createView(center, zoom, projection);

	this._map = new Map({
		view:     view,
		controls: [],
		target:   options.target
	});

	// wird getriggert, wenn die Karte gezoomt oder verschoben wurde (auch bei Rotation), d.h. Aktionen sind dann
	// bereits abgeschlossen, auch bei Animationen
	this._map.on('moveend', function() {
		const viewport = self.getViewport();
		self._fireEvent( gc.gis.map.GCMapEvent.VIEWPORT_CHANGED, {
			viewport: viewport
		});

		self._fireEvent( gc.gis.map.GCMapEvent.VIEWPORT_CHANGEEND, {
			viewport: viewport
		});
	});

    // Klicks
	// A true single click with no dragging and no double click.
    this._map.on('singleclick', function(evt) {
        self._fireEvent(gc.gis.map.GCMapEvent.SINGLECLICK, {
            coordinate: evt.coordinate,
            pixel: evt.pixel,
            dragging: evt.dragging
        });
    });
    // A true double click, with no dragging.
    this._map.on('dblclick', function(evt) {
        self._fireEvent(gc.gis.map.GCMapEvent.DBLCLICK, {
            coordinate: evt.coordinate,
            pixel: evt.pixel,
            dragging: evt.dragging
        });
    });
    // A click with no dragging. A double click will fire two of this.
    this._map.on('click', function(evt) {
        self._fireEvent(gc.gis.map.GCMapEvent.CLICK, {
            coordinate: evt.coordinate,
            pixel: evt.pixel,
            dragging: evt.dragging
        });
    });

	// Rotation
	// FIXME: change:rotation wird doch gar nicht von der Map gefeuert
	this._map.on('change:rotation', function() {
		self._fireEvent( gc.gis.map.GCMapEvent.ROTATION_CHANGED, {
			rotation: self.getRotation()
		});
	});

	// Größenänderung
	this._map.on('change:size', function() {
		if (self._maxExtent && !self._constrainOnlyCenter)
		{
			// Gebietsbeschränkung anpassen (insbesondere max. Auflösung)
			self.__reinitView();
		}
	});

	// Mausbewegungen
	this._map.getViewport().addEventListener('mouseleave', function (evt) {
		self._fireEvent(gc.gis.map.GCMapEvent.MOUSE_LEAVE, evt);
	});
	this._map.getViewport().addEventListener('mouseenter', function (evt) {
		self._fireEvent(gc.gis.map.GCMapEvent.MOUSE_ENTER, evt);
	});
	this._map.on('pointermove', function(evt) {
		self._fireEvent(gc.gis.map.GCMapEvent.POINTER_MOVE, evt);
	});

	// Tooltip initialisieren
	this._tooltip = new gc.gis.OLMapTooltip(this._map);
};

/**
 *
 * @param {number[]} center
 * @param {number} zoom
 * @param {ol.proj.Projection} projection
 * @return {ol.View}
 * @private
 */
gc.gis.OLMap.prototype.__createView = function(center, zoom, projection)
{
	const self = this;
	const options = {
		resolutions: this.__getResolutions(projection),
		//constrainResolution: true,	// nicht aktivieren Zoom-Aktionen werden damit ungeschmeidig
		projection: projection,
		center: center,
		zoom: zoom
	};

	if (this._maxExtent)
	{
		options.extent = this.transformExtent(this._maxExtent, this._maxExtentProjection, projection);
		options.constrainOnlyCenter = this._constrainOnlyCenter;
		options.showFullExtent = true;
	}

	const view = new View(options);

	unByKey('change:resolution');
	unByKey('change:rotation');

	view.on('change:resolution', function() {
		if (self._map)
		{
			self._fireEvent( gc.gis.map.GCMapEvent.SCALE_CHANGED, {
				newValue: self.getScale()
			});
			self._fireEvent( gc.gis.map.GCMapEvent.VIEWPORT_CHANGED, {
				viewport: self.getViewport()
			});
		}
	});
	view.on('change:rotation', function() {
		if (self._map)
		{
			self._fireEvent( gc.gis.map.GCMapEvent.ROTATION_CHANGED, {
				rotation: self.getRotation()
			});
			self._fireEvent( gc.gis.map.GCMapEvent.VIEWPORT_CHANGED, {
				viewport: self.getViewport()
			});
		}
	});

	return view;
};

/**
 * @inheritDoc
 */
gc.gis.OLMap.prototype.supportsCRS = function(crsName)
{
	return !!proj4.defs(crsName);
};

/**
 * @inheritDoc
 */
gc.gis.OLMap.prototype.getSize = function()
{
    return this._map.getSize() || [0,0];
};

/**
 * @inheritDoc
 */
gc.gis.OLMap.prototype.updateSize = function()
{
	if( this._map )
	{
        this._map.updateSize();
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getImpl = function()
{
	return this._map;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getLayerFactory = function()
{
    return this._layer_factory;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.addLayer = function( layer )
{
	this._managed_layers.getLayers().push( layer );

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.insertLayerAt = function( layer, index )
{
	this._managed_layers.getLayers().insertAt( index, layer );

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.removeLayer = function( layer )
{
	this._managed_layers.getLayers().remove( layer );

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.removeAllLayers = function()
{
	this._managed_layers.getLayers().clear();

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getLayers = function()
{
	return this._managed_layers.getLayers();
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.setBackgroundLayer = function(layer)
{
	const layerCollection = this._backgroundLayers.getLayers();
	layerCollection.clear();
	layerCollection.push(layer);

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.unsetBackgroundLayer = function()
{
	const layerCollection = this._backgroundLayers.getLayers();
	layerCollection.clear();

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.removeBackgroundLayer = function(layer)
{
	const layerCollection = this._backgroundLayers.getLayers();
	layerCollection.remove(layer);

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.reloadLayers = function(opt_image, opt_tile, opt_vector)
{
	const include_image = opt_image !== false,
		include_tile = opt_tile !== false,
		include_vector = opt_vector !== false,
		layers = this._managed_layers.getLayers();
	let layer, source, i, ii;

	for (i = 0, ii = layers.getLength(); i < ii; i++)
	{
		layer = layers.item(i);
		if (layer instanceof ImageLayer && include_image)
		{
			source = layer.getSource();
			// die Funktion "updateParams" steht eigentlich in allen dienstbasierten Bild-Layern zur Verfügung
			// Bild-Layer mit statischen Bildern werden nicht neu geladen (sollte aber auch nicht notwendig sein)
			if (typeof source.updateParams === 'function')
			{
				//source.updateParams(source.getParams());
				source.updateParams({"__time__": Date.now()});
			}
		}
		else if (layer instanceof VectorLayer && include_vector)
		{
			// clear() zieht ein Reload der Daten von der Datenquelle nach sich
			source = layer.getSource();
			source.clear();
		}
		else if (layer instanceof TileLayer && include_tile)
		{
			// ???
		}
		// weitere ...
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.addTrackingLayer = function(layer)
{
	gc.gis.assert.isInstance(layer, gc.gis.layer.OLTrackingLayer);

	this._tracking_layers.getLayers().push(layer.getImpl());
	this._fireEvent(gc.gis.map.GCMapEvent.TRACKINGLAYER_ADDED, {
		layer: layer
	});

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.removeTrackingLayer = function(layer)
{
	gc.gis.assert.isInstance(layer, gc.gis.layer.OLTrackingLayer);

	this._tracking_layers.getLayers().remove(layer.getImpl());
	this._fireEvent(gc.gis.map.GCMapEvent.TRACKINGLAYER_REMOVED, {
		layer: layer
	});

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.removeAllTrackingLayers = function()
{
	this._tracking_layers.getLayers().clear();
	this._fireEvent(gc.gis.map.GCMapEvent.TRACKINGLAYER_REMOVED_ALL, {});

	return this;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.numTrackingLayersFeatures = function()
{
	let num = 0;
	this._tracking_layers.getLayers().forEach(function (lyr) {
		if (lyr.getVisible())
		{
			num += lyr.getSource().getFeatures().length;
		}
	});

	return num;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getHighlightLayer = function()
{
	return this._highlight_layer;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.panTo = function(position, opt_projection)
{
	gc.gis.assert.isArray(position);

	if (opt_projection && opt_projection !== this.getProjection())
	{
		// gc.gis.iface.log.debug( this, 'Transforming from '+ opt_projection +' to '+ this.getProjection() );
		position = olProj.transform(position, olProj.get( opt_projection ), this._map.getView().getProjection());
	}

	if (this.__animateMapAction)
	{
        this.__animatedPanTo(position);
    }
    else
    {
        this._map.getView().setCenter(position);
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.fit = function (geometryOrExtent, opt_options)
{
	const self = this;
	let constrain_resolution = false;
    let projection = undefined;
    let minRes = 0;
    let duration = 1000;

    if (opt_options)
    {
        if (opt_options.constrain_resolution && opt_options.constrain_resolution === true) {
            constrain_resolution = true;
        }
        if (opt_options.projection) {
            projection = olProj.get(opt_options.projection);
		}
		if (opt_options.minscale && opt_options.minscale > 0) {
			minRes = this.__scaleToResolution(opt_options.minscale);
		}
	    if (opt_options.duration !== undefined && opt_options.duration >= 0) {
		    duration = opt_options.duration;
	    }
	}

    if (typeof geometryOrExtent === "string")
	{
		geometryOrExtent = this.__parseGeometry(geometryOrExtent, gc.gis.format.WKT);
	}
	else if (typeof geometryOrExtent === "object" && geometryOrExtent instanceof gc.gis.geom.GCGeometry)
	{
        projection = olProj.get(geometryOrExtent.getCRS()) || projection; // hier hat CRS der Geometrie Vorrang
		geometryOrExtent = geometryOrExtent.getImpl();
	}

	if (Array.isArray(geometryOrExtent) || geometryOrExtent instanceof SimpleGeometry)
	{
		// ggf. transformieren
        if(projection && projection.getCode() !== this.getProjection())
        {
            // gc.gis.iface.log.debug(this, 'Transforming from '+ projection.getCode() +' to '+ this.getProjection());
			if (Array.isArray(geometryOrExtent)) {
                geometryOrExtent = olProj.transformExtent(
                    geometryOrExtent,
                    projection, this._map.getView().getProjection()
                );
			}
			else {
                geometryOrExtent.transform(projection, this._map.getView().getProjection());
			}
        }

        // Kartenausschnitt anpassen
        const fit_options = {
            size: this._map.getSize(),
            padding: [0,0,0,0],
            minResolution: minRes
		};

        if (constrain_resolution)
		{
			this._map.getView().setConstrainResolution(true);
			fit_options.callback = function () {
				self._map.getView().setConstrainResolution(false);
			}
		}

        if (this.__animateMapAction) {
            fit_options.duration = duration;
        }

        this._map.getView().fit(geometryOrExtent, fit_options);
	}
	else
	{
        gc.gis.iface.log.error(this, 'Ungültiges Objekt zur Anpassung des Kartenausschnitts.');
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.transformExtent = function(extent, source_crs, target_crs)
{
	if(!extent)
	{
		throw new gc.gis.error.GCError('Fehler beim Transformieren: Ausdehnung ist nicht definiert.');
	}
	else if(!source_crs)
	{
		gc.gis.iface.log.warn(this, 'Ausdehnung konnte nicht transformiert werden: Quellkoordinatenreferenzsystem ist nicht gesetzt. Es kann ggf. zu Darstellungsfehlern kommen.');
	}
	else if(!target_crs)
	{
		gc.gis.iface.log.warn( this, 'Ausdehnung konnte nicht transformiert werden: Zielkoordinatenreferenzsystem ist nicht gesetzt. Es kann ggf. zu Darstellungsfehlern kommen.');
	}
	else if(!olProj.get(source_crs))
	{
		gc.gis.iface.log.warn( this, 'Ausdehnung konnte nicht transformiert werden: Unbekanntes Quellkoordinatenreferenzsystem: "'+ source_crs +'". Es kann ggf. zu Darstellungsfehlern kommen.');
	}
	else if(!olProj.get(target_crs))
	{
		gc.gis.iface.log.warn( this, 'Ausdehnung konnte nicht transformiert werden: Unbekanntes Zielkoordinatenreferenzsystem: "'+ target_crs +'". Es kann ggf. zu Darstellungsfehlern kommen.');
	}
	else if(source_crs !== target_crs)
	{
		return olProj.transformExtent(extent, source_crs, target_crs);
	}
	return extent;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getRotation = function()
{
	return this._map.getView().getRotation() * (180/Math.PI);
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.setRotation = function(degrees)
{
    if (this.__animateMapAction) {
        this.__animatedSetRotation(degrees);
    }
    else {
        this._map.getView().setRotation(degrees * (Math.PI / 180));
    }
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getProjection = function()
{
	return this._map.getView().getProjection().getCode();
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.setProjection = function(projection)
{
	const old_projection = this.getProjection();

	if(projection && old_projection !== projection)
	{
		const ol_projection = olProj.get(projection);

		if(ol_projection)
		{
			this.__reinitView(ol_projection);

			//	OpenLayers transformiert gesetzte Layer-Extents nicht, wieso auch...
			const transformLayerExtent = function(layer)
			{
				if (typeof layer.getExtent() !== 'undefined')
				{
					const newExtent = olProj.transformExtent(layer.getExtent(), old_projection, projection);
					layer.setExtent(newExtent);
				}

				if (typeof layer.getLayers === 'function')
				{
					const childLayers = layer.getLayers();
					childLayers.forEach(transformLayerExtent);
				}
			};

			this._map.getLayers().forEach(transformLayerExtent);
		}
		else
		{
			throw new gc.gis.error.GCProjectionError('Unknown CRS definition: '+ projection);
		}
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getCenter = function()
{
	return this._map.getView().getCenter();
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getZoom = function()
{
	return this._map.getView().getZoom();
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.zoomIn = function()
{
	let zoom = this.getZoom() + 1;
	zoom = Math.floor(zoom);	// vorgegebene Auflösungen anspringen (keine Zwischenzoomstufen)

	this.__animateMapAction ? this.__animatedZoom(zoom, 250) : this._map.getView().setZoom(zoom);
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.zoomOut = function()
{
	let zoom = this.getZoom() - 1;
	zoom = Math.ceil(zoom);		// vorgegebene Auflösungen anspringen (keine Zwischenzoomstufen)

	this.__animateMapAction ? this.__animatedZoom(zoom, 250) : this._map.getView().setZoom(zoom);
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getScale = function()
{
	const dpm = gc.gis.iface.env.dpm;
	const mpu = this._map.getView().getProjection().getMetersPerUnit();
	const resolution = this._map.getView().getResolution();

	return Math.round(resolution * mpu * dpm);
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.setScale = function(scale)
{
	const resolution = this.__scaleToResolution(scale);
	if (this._map.getView().getResolution() !== resolution)
	{
		if (this.__animateMapAction)
		{
			this.__animatedSetResolution(resolution);
		}
		else
		{
			this._map.getView().setResolution(resolution);
		}
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getViewportExtent = function ()
{
	return this._map.getView().calculateExtent(this._map.getSize());
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.getViewport = function()
{
	return {
		rotation: this.getRotation(),
		geometry: this.__getViewportGeometry(),
		projection: this.getProjection()
	};
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.setViewport = function(viewport, animate)
{
	animate = animate !== false;

	// die Animation darf nur deaktiviert werden, sofern der globale Schalter aktiv ist
	// (eine Aktivierung obwohl global keine Animation gewünscht ist, soll nicht möglich sein)
	const old_animate = this.__animateMapAction;
	if (!animate && this.__animateMapAction)
	{
		this.__animateMapAction = false;
	}

	if(viewport.projection && viewport.projection !== this.getProjection())
	{
		this.getMapView().setProjection(viewport.projection);
	}

	if(viewport.position && viewport.position !== this.getCenter())
	{
		if(viewport.projection && viewport.projection !== this.getProjection())
		{
			// gc.gis.iface.log.debug( this, 'Transforming from '+ viewport.projection +' to '+ this.getProjection() );
			viewport.position = olProj.transform(viewport.position, olProj.get(viewport.projection), this._map.getView().getProjection());
		}

		this.panTo(viewport.position);
	}

	if(viewport.rotation && viewport.rotation !== this.getRotation())
	{
		this.setRotation(viewport.rotation);
	}

	if(viewport.geometry && viewport.geometry !== this.__getViewportGeometry())
	{
		this.fit(viewport.geometry, {
			duration: this.__animateMapAction ? 1000 : 0
		});
	}

	if(viewport.scale && viewport.scale !== this.getScale())
	{
		this.setScale(viewport.scale);
	}

	this.__animateMapAction = old_animate;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.showMarker = function(message, position, opt_options)
{
	const self = this;

	opt_options = opt_options || {};
	if(opt_options.projection && opt_options.projection !== this.getProjection())
	{
		position = olProj.transform(position, olProj.get(opt_options.projection), this._map.getView().getProjection());
	}

	if (!this._internalIntersectsMaxExtent(new olGeom.Point(position), this._map.getView().getProjection()))
	{
		throw new gc.gis.error.GCOutOfMaxExtentError('Die angegebene Position liegt außerhalb des sichtbaren Bereichs.');
	}

	const element = jQuery('<div>', {
		'class': 'gc-map-overlay',
		html: '<p class="gc-map-overlay-message">'+ message +'</p>'
	});

	const popup = new Overlay({
		element: element[0],
		position: position
		//stopEvent: false  // TODO überarbeiten (Schließen und Co.)
	});
	this._map.addOverlay(popup);

	const btn_close = jQuery('<button>', {
		'class': 'gc-map-overlay-button-close',
		html: '<span class="gc-icon glyphicon glyphicon-remove"></span><span>Schließen</span>',
		click: function(evt) {
			evt.preventDefault();

			if (typeof opt_options.close === 'function')
			{
				opt_options.close.call(self, popup, evt);
			}

			try
			{
                self._map.removeOverlay(popup);
			}
			catch( err )
			{
				gc.gis.iface.log.error(self, err);
			}
			jQuery(this).blur();
		}
	});
	element.append( btn_close );

	if (opt_options.enter && typeof opt_options.enter === 'function') {
		element.mouseenter(function (evt) {
			opt_options.enter.call(self, popup, evt);
		});
	}

	if (opt_options.leave && typeof opt_options.leave === 'function') {
		element.mouseleave(function (evt) {
			opt_options.leave.call(self, popup, evt);
		});
	}

	return element;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.highlightGeometry = function(geometry, format, opt_options)
{
	opt_options = opt_options || [];
	this._internalHighlightGeometry(geometry, format, opt_options);
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.highlightGeometryWithMarker = function (geometry, format, message, opt_options)
{
	opt_options = opt_options || [];

	const self = this;
	let m_pos = opt_options.markerPosition;
	const m_proj = opt_options.markerProjection;
	const feature = this._internalHighlightGeometry(geometry, format, opt_options);

	if (!m_pos && feature)
	{
		m_pos = this.__calcMarkerPosition(feature.getGeometry());
	}

	if (m_pos)
	{
		const options = {
			projection: m_proj
		};

		if (feature)
		{
			// beim Schließen Feature ausblenden
			options.close = function (popup, evt) {
				try {
					self._highlight_layer.getImpl().getSource().removeFeature(feature);
				}
				catch (err) {
					// do nothing: Fehler falls Feature nicht in Source gefunden wird
				}
			};

			// beim Hover Feature hervorheben
			options.enter = function (popup, evt)
			{
				let style = this._style_factory.getStyle(gc.gis.factory.StyleType.HIGHLIGHT_ACTIVE);
				if (opt_options.styleActive)
				{
					style = opt_options.styleActive;
				}

				feature.setStyle(style);
			};

			// beim Verlassen Feature im Standard-Style anzeigen
			options.leave = function (popup, evt)
			{
				let style = null;
				if (opt_options.style)
				{
					style = opt_options.style;
				}

				feature.setStyle(style);
			};

		}
		const element = this.showMarker(message, m_pos, options);
		feature.set('gc_marker_element', element, true);    // siehe __initLayers, Listener zum aufräumen von Overlays
	}
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.showMouseTooltip = function(text)
{
	this._tooltip.show(text);
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.disableMouseTooltip = function()
{
	this._tooltip.hide();
};

/**
 * Prüft, ob die übergebene Geometrie mit dem eingestellten max. Extent eine Überschneidung hat. Sofern kein max. Extent
 * eingestellt ist, wird stets <code>true</code> zurückgegeben.
 *
 * @param {gc.gis.geom.GCGeometry} geometry
 * @return {boolean}
 */
gc.gis.OLMap.prototype.intersectsMaxExtent = function(geometry)
{
	return this._internalIntersectsMaxExtent(geometry.getImpl(), geometry.getCRS());
};

/**
 * Hebt eine Geometrie auf der Karte hervor und liefert das korrespondierende Feature.
 *
 * @param geometry
 * @param format
 * @param opt_options
 * @return {ol.Feature|null}
 * @private
 */
gc.gis.OLMap.prototype._internalHighlightGeometry = function(geometry, format, opt_options)
{
	/*
	 *	FIXME: Derzeit beherrscht ol3 (3.7.0) noch keine clientseitige
	 *	on-the-fly-Projektion. Pläne dafür existieren:
	 *	- für Vektor: ???
	 *	- für Raster: https://github.com/openlayers/ol3/issues/3785
	 *
	 *	Als Workaround müsste man den Highlight-Layer bei Projektionsänderungen
	 *	des ol.View's mit einer neuen ol.source.Vector.projection ausstatten bzw.
	 *	wie in dieser Funktion mit
	 *	  ol.format.WKT.readGeometry(..., {featureProjection: 'EPSG:target'})
	 *	neu parsen.
	 */

	let feature;

	// ggf. altes Highlighting löschen
	if (opt_options.clear !== false)
	{
		this.clearHighlighting();
	}

	geometry = this.__parseGeometry(geometry, format, opt_options.projection);

	if (!this._internalIntersectsMaxExtent(geometry, this._map.getView().getProjection()))
	{
		throw new gc.gis.error.GCOutOfMaxExtentError('Die angegebene Geometrie liegt außerhalb des sichtbaren Bereichs.');
	}

	if(geometry)
	{
		// Sonderbehandlung Polygon ohne Ausdehnung
		if (geometry instanceof olGeom.Polygon)
		{
			const extent = geometry.getExtent();
			if (extent[0] === extent[2] && extent[1] === extent[3])
			{
				geometry = geometry.getInteriorPoint();
			}
		}

		feature = new Feature({
			geometry: geometry
		});

		if (opt_options.style)
		{
			feature.setStyle(opt_options.style);
		}

		this._highlight_layer.getImpl().getSource().addFeature(feature);
	}

	return feature;
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.clearHighlighting = function()
{
	this._highlight_layer.getImpl().getSource().clear();
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.pixelToWorld = function( pixel )
{
	return this._map.getCoordinateFromPixel( pixel );
};

/**
 * @inheritdoc
 */
gc.gis.OLMap.prototype.worldToPixel = function( coordinate )
{
	return this._map.getPixelFromCoordinate( coordinate );
};

/**
 * @inheritDoc
 */
gc.gis.OLMap.prototype.dumpCanvasTrackingLayer = function(format)
{
	// FIXME: anstatt JQueryPromise die konkreten Daten zurückgeben (Problem ansynchrones Rendering)
	const self = this;
	const dfd = jQuery.Deferred();

	this._dumpCanvasMap(self._map, format, function (data) {
		self._managed_layers.setVisible(true);
		dfd.resolve(data);
	});
	self._managed_layers.setVisible(false);
	//self._map.renderSync();

	return dfd.promise();
};

/**
 * @inheritDoc
 */
gc.gis.OLMap.prototype.dumpCanvasTrackingLayerByBox = function(box, size, format)
{
	// FIXME: anstatt JQueryPromise die konkreten Daten zurückgeben (Problem ansynchrones Rendering)
	const dfd = jQuery.Deferred();

	// temp. Element erzeugen und in unsichtbaren Bereich verschieben
	const target = this._map.getTargetElement().id + Date.now();
	const map_element = jQuery('<div>', {
		id: target
	});
	map_element.css('width', size[0]);
	map_element.css('height', size[1]);
	map_element.css('position', 'absolute');
	map_element.css('left', -Math.ceil(size[0] * 1.1));
	map_element.css('top', 0);
	map_element.appendTo('body');

	// temp. OL-Map mit Tracking-Layern erzeugen
	let map = new Map({
		target: target,
		layers: [this._tracking_layers],
		view: new View({
			projection: this._map.getView().getProjection()
		})
	});

	this._dumpCanvasMap(map, format, function (data) {
		map = null;
		map_element.remove();
		dfd.resolve(data);
	});
	map.getView().fit(box);

	return dfd.promise();
};

/**
 *
 * @param {ol.Map} map
 * @param {string} format
 * @param {function} result
 * @private
 */
gc.gis.OLMap.prototype._dumpCanvasMap = function(map, format, result)
{
	map.once('rendercomplete', function() {
		const size = map.getSize();
		const mapCanvas = document.createElement('canvas');
		mapCanvas.width = size[0];
		mapCanvas.height = size[1];
		const mapContext = mapCanvas.getContext('2d');

		Array.prototype.forEach.call(map.getTargetElement().querySelectorAll('.ol-layer canvas'), function(canvas) {
			if (canvas.width > 0)
			{
				const opacity = canvas.parentNode.style.opacity;
				const transform = canvas.style.transform;
				// Get the transform parameters from the style's transform matrix
				const matrix = transform.match(/^matrix\(([^\(]*)\)$/)[1].split(',').map(Number);

				mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
				// Apply the transform to the export map context
				CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
				mapContext.drawImage(canvas, 0, 0);
			}
		});

		result(mapCanvas.toDataURL(format));
	});
};

/**
 * Initialisiert die Openlayers-Gruppenlayer für Normale und Trackinglayer.
 *
 * @private
 */
gc.gis.OLMap.prototype.__initLayers = function()
{
	const self = this;

	// Layer-Gruppe initialisieren
	this._managed_layers = new LayerGroup();
	this._tracking_layers = new LayerGroup();
	this._backgroundLayers = new LayerGroup();

	// Gruppenlayer hinzufügen
	this._map.addLayer(this._backgroundLayers);
	this._map.addLayer(this._managed_layers);
	this._map.addLayer(this._tracking_layers);

	// Highlight-Layer initialisieren
	this._highlight_layer = new gc.gis.layer.OLTrackingLayer(this, {
		allowClearDrawingArea: true
	});
	const highlightSource = this._highlight_layer.getImpl().getSource();
	highlightSource.on('removefeature', function(evt) {
		// ggf. Overlay zum Feature entfernen
		self._map.getOverlays().forEach(function (overlay) {
			if (overlay instanceof Overlay)
			{
				// siehe highlightGeometryWithMarker (Erzeugung des Features)
				if (overlay.getElement() === evt.feature.get('gc_marker_element').get(0))
				{
					try {
						self._map.removeOverlay(overlay);
					}
					catch (err) {}
				}
			}
		});
	});

	this._highlight_layer.setStyle(this._style_factory.getStyle(gc.gis.factory.StyleType.HIGHLIGHT));
	this.addTrackingLayer(this._highlight_layer);
};

/**
 * Initialisiert die Openlayer-Interaktions-Events.
 *
 * @private
 */
gc.gis.OLMap.prototype.__initInteractions = function()
{
	const self = this;
	let i, cur_idx;

	this._map.getInteractions().on('add', function( evt ) {	// FIXME: remove implementieren, falls mal jemand dynamisch interactions entfernt
		const interaction = evt.element;
		interaction.on('change:active', function( evt2 ) {
			if( evt2.target.getActive() === true && evt2.oldValue === false )
			{
				self._fireEvent( gc.gis.map.GCMapEvent.INTERACTION_ACTIVATED, {
					interaction: interaction
				});
				delete evt2.target._gc_history;

				// Alle anderen aktivieren
				const iacts = self._map.getInteractions().getArray();
				for(i=0; i<iacts.length; i++)
				{
					if( iacts[i].get('_is_gc')
						&& iacts[i] !== evt2.target
						&& iacts[i].getActive() === true )
					{
						iacts[i]._gc_history = true;
						iacts[i].setActive( false );
						self._last_interactions.push( iacts[i] );
					}
				}
			}
			else if( evt2.target.getActive() === false && evt2.oldValue === false )
			{
				cur_idx = self._last_interactions.indexOf( evt2.target );
				if( cur_idx >= 0 )
				{
					self._last_interactions.splice( cur_idx, 1 );
				}
			}
			else if( evt2.target.getActive() === false && evt2.oldValue === true )
			{
				self._fireEvent( gc.gis.map.GCMapEvent.INTERACTION_DEACTIVATED, {
					interaction: interaction
				});
				if( self._last_interactions.length > 0 )
				{
					let last_idx = self._last_interactions.length-1;
					cur_idx = self._last_interactions.indexOf(evt2.target);
					if( !evt2.target._gc_history && last_idx >= 0 )
					{
						let last_iact = self._last_interactions[last_idx];
						last_iact.setActive( true );
						self._last_interactions.splice( last_idx, 1 );
					}
					if( cur_idx >= 0 )
					{
						self._last_interactions.splice( cur_idx, 1 );
					}
				}
			}

			// DEBUG
//			const queued = [];
//			const count = self._last_interactions.length;
//			for(i=0; i<count; i++)
//			{
//				queued.push(self._last_interactions[i].get('_gc_name'));
//			}
		});
	});
};

/**
 * Aktualisiert das View-Objekt der OpenLayers-Map. Dabei wird ggf. die Projektion geändert. Zudem werden die Gebietsfilter
 * angewendet. Die Funktion wird beispielsweise aufgerufen, wenn die Projektion geändert wurde oder ein Resize der
 * Karte stattgefunden hat.
 *
 * @param {Projection|string} [opt_projection]
 * @private
 * @throws {gc.gis.error.GCProjectionError}
 */
gc.gis.OLMap.prototype.__reinitView = function(opt_projection)
{
	const ol_old_projection = this._map.getView().getProjection();
	let ol_new_projection = opt_projection || ol_old_projection;

	if (typeof ol_new_projection === 'string')
	{
		ol_new_projection = olProj.get(ol_new_projection);
	}

	if (!ol_new_projection)
	{
		throw new gc.gis.error.GCProjectionError('Unknown CRS definition: '+ opt_projection);
	}

	const view = this.__createView(
		olProj.transform(this._map.getView().getCenter(), ol_old_projection, ol_new_projection),
		this._map.getView().getZoom(),
		ol_new_projection
	);

	this._map.setView(view);
};

/**
 * Prüft, ob die übergebene Geometrie mit dem eingestellten max. Extent eine Überschneidung hat. Sofern kein max. Extent
 * eingestellt ist, wird stets <code>true</code> zurückgegeben.
 *
 * @param {ol.geom.Geometry} geometry
 * @param {string|ol.proj.Projection} projection
 * @return {boolean}
 */
gc.gis.OLMap.prototype._internalIntersectsMaxExtent = function(geometry, projection)
{
	let ret = true;

	if (this._maxExtent && !this._constrainOnlyCenter)
	{
		let extent = this._maxExtent;

		// LinearRing kennt intersectsExtent-Methode nicht
		if (geometry instanceof olGeom.LinearRing)
		{
			geometry = new olGeom.Polygon([geometry.getCoordinates()]);
		}

		extent = this.transformExtent(extent, this._maxExtentProjection, projection);
		ret = geometry.intersectsExtent(extent);
	}

	return ret;
};


/**
 * Rechnet eine Maßstabszahl in eine Openlayers-Auflösung um.
 *
 * @private
 * @param {number} scale Maßstabszahl
 * @param {ol.proj.Projection} [opt_ol_proj]
 * @return {number} Openlayers-Auflösung
 */
gc.gis.OLMap.prototype.__scaleToResolution = function(scale, opt_ol_proj)
{
	const dpm = gc.gis.iface.env.dpm;
	const mpu = opt_ol_proj ? opt_ol_proj.getMetersPerUnit() : this._map.getView().getProjection().getMetersPerUnit();
	const norm_scale = 1 / scale;

	return 1 / (norm_scale * mpu * dpm);
};

/**
 * Gibt eine Liste von Auflösungen anhand der verfügbaren Maßstäbe zurück. Dabei wird ggf. auch eine Gebietsbeschränkung
 * berücksichtigt.
 *
 * @private
 * @param {ol.proj.Projection|string} projection
 * @returns {Number[]|undefined}
 */
gc.gis.OLMap.prototype.__getResolutions = function(projection)
{
	const list = [];

	if(typeof projection === 'string')
	{
		projection = olProj.get(projection);
	}

	const maxRes = this.__getMaxExtentResolution(projection) || Number.MAX_VALUE;
	const items = this.getMapView().getAvailableScales();

	let i, res;
	for(i = 0; i < items.length; i++)
	{
		res = this.__scaleToResolution(items[i].value, projection);
		if (res < maxRes)
		{
			list.push(res);
		}
		else
		{
			list.push(maxRes);
			break;
		}
	}
	list.reverse();

	return list.length > 0 ? list : undefined;
};

/**
 * Liefert die max. Auflösung in Abhängigkeit der aktuellen Größe des Map-Target, sofern eine Gebietsbeschränkung angegeben wurde,
 * ansonsten <code>undefined</code>.
 *
 * @param {string|ol.proj.Projection} projection
 * @return {Number|undefined}
 * @private
 */
gc.gis.OLMap.prototype.__getMaxExtentResolution = function(projection)
{
	let	res = undefined;
	const element = document.getElementById(this._target);

	if (element && this._maxExtent && !this._constrainOnlyCenter)
	{
		const extent = this.transformExtent(this._maxExtent, this._maxExtentProjection, projection);
		const metrics = getComputedStyle(element);
		const size = [];

		size[0] = parseInt(metrics.width, 10);
		size[1] = parseInt(metrics.height, 10);

		const xResolution = (extent[2] - extent[0]) / size[0];
		const yResolution = (extent[3] - extent[1]) / size[1];

		res = Math.max(xResolution, yResolution);
	}

	return res;
};


/**
 * Gibt die aktuelle Geometrie des Kartenausschnitts als WKT zurück.
 *
 * @private
 * @return {string} WKT-Geometrie des aktuellen Kartenausschnitts
 */
gc.gis.OLMap.prototype.__getViewportGeometry = function()
{
	const size = this._map.getSize() || [0,0];
	const tl = this.pixelToWorld( [0,      0] );
	const tr = this.pixelToWorld( [size[0],0] );
	const br = this.pixelToWorld( [size[0],size[1]] );
	const bl = this.pixelToWorld( [0,      size[1]] );

	if( tl && tl.length > 0 )
	{
		return 'POLYGON(('
			+ tl[0] +' '+ tl[1] +','
			+ tr[0] +' '+ tr[1] +','
			+ br[0] +' '+ br[1] +','
			+ bl[0] +' '+ bl[1] +','
			+ tl[0] +' '+ tl[1]
			+'))';
	}
	return null;
};

/**
 * Ermittelt aus einer Geometrie eine passende Position für einen Marker.
 *
 * @private
 * @param {number[]|ol.geom.Geometry} geometry
 * @returns {number[]|undefined} Position
 */
gc.gis.OLMap.prototype.__calcMarkerPosition = function(geometry)
{
	let point;

	if (geometry instanceof olGeom.MultiPoint && geometry.getPoints().length > 0)
	{
		geometry = geometry.getPoint(0);
	}
	else if (geometry instanceof olGeom.MultiLineString && geometry.getLineStrings().length > 0)
	{
		geometry = geometry.getLineString(0);
	}
	else if (geometry instanceof olGeom.MultiPolygon && geometry.getPolygons().length > 0)
	{
		let geom, area, ref_area = -1.0;

		for (let i=0; i<geometry.getPolygons().length; i++)
		{
			area = geometry.getPolygon(i).getArea();
			if (area > ref_area)
			{
				ref_area = area;
				geom = geometry.getPolygon(i);
			}
		}
		geometry = geom;
	}
	else if (geometry instanceof olGeom.GeometryCollection && geometry.getGeometries().length > 0)
	{
		geometry = geometry.getGeometries()[0];
	}

	if (geometry instanceof olGeom.Polygon)
	{
		point = geometry.getInteriorPoint();
		if (point)
		{
			return point.getCoordinates();
		}
	}
	else if (geometry instanceof olGeom.LineString)
	{
		geometry = geometry.getCoordinateAt(0.5);   // liefert {Coordinate} d.h. {Array.<number>}
	}


	if (geometry instanceof olGeom.Geometry)
	{
		geometry = geometry.getExtent();
	}

	if (Array.isArray(geometry))
	{
		if (geometry.length === 4)
		{
			return [
				Math.min(geometry[0], geometry[2]) + Math.abs(geometry[0] - geometry[2]) / 2.0,
				Math.min(geometry[1], geometry[3]) + Math.abs(geometry[1] - geometry[3]) / 2.0
			];
		}
		else if (geometry.length === 2)
		{
			return geometry;
		}
	}

	return undefined;
};

/**
 * Verschiebt die Karte und führt dazu eine Animation durch.
 *
 * @param {Number[]} position
 * @param {int} [opt_duration]
 * @private
 */
gc.gis.OLMap.prototype.__animatedPanTo = function(position, opt_duration)
{
    const duration = opt_duration || 1000;
	const animation = {
        center: position,
        duration: duration
    };

    this._map.getView().animate(animation);
};

/**
 * Setzt das Zoomlevel und führt dazu eine Animation durch.
 *
 * @param {int} zoom_level
 * @param {int} [opt_duration]
 * @private
 */
gc.gis.OLMap.prototype.__animatedZoom = function(zoom_level, opt_duration)
{
	const duration = opt_duration || 250;

	const animation = {
        zoom: zoom_level,
        duration: duration
    };

    this._map.getView().animate(animation);
};

/**
 * Setzt die Auflösung und führt dazu eine Animation durch.
 *
 * @param {Number} resolution
 * @param {int} [opt_duration]
 * @private
 */
gc.gis.OLMap.prototype.__animatedSetResolution = function(resolution, opt_duration)
{
	const duration = opt_duration || 1000;

	const animation = {
        resolution: resolution,
        anchor: this._map.getView().getCenter(),
        duration: duration
    };

    this._map.getView().animate(animation);
};

/**
 * Setzt die Rotation und führt dazu eine Animation durch.
 *
 * @param {Number} degrees
 * @param {int} [opt_duration]
 * @private
 */
gc.gis.OLMap.prototype.__animatedSetRotation = function(degrees, opt_duration)
{
	const duration = opt_duration || 250;

	const animation = {
        rotation: degrees * (Math.PI/180),
        anchor: this._map.getView().getCenter(),
        duration: duration
    };

    this._map.getView().animate(animation);
};


/**
 * Parst eine Geometrie und führt dabei ggf. eine Projektion in das CRS der MapView durch.
 *
 * @param {string|ol.geom.Geometry|gc.gis.geom.GCGeometry} geometry
 * @param {string} format
 * @param {string} [opt_projection]
 * @return {ol.geom.Geometry}
 * @private
 * @throws gc.gis.error.GCError im Falle eines Fehlers beim Parsen
 */
gc.gis.OLMap.prototype.__parseGeometry = function(geometry, format, opt_projection)
{
	let ol_geom = undefined;
	let ol_format = undefined;
	let ol_format_options = {};

	if (geometry instanceof gc.gis.geom.GCGeometry)
	{
		geometry = geometry.getImpl();
	}

	if (geometry instanceof olGeom.Geometry)
	{
		if(opt_projection && opt_projection.toLowerCase() !== this.getProjection().toLowerCase())
		{
			geometry.transform(olProj.get(opt_projection), this._map.getView().getProjection());
		}

		return geometry;
	}

	// ggf. Projektion angeben
	if(opt_projection)
	{
		ol_format_options = {
			dataProjection:    opt_projection,
			featureProjection: this._map.getView().getProjection()
		};
	}

	if(format === gc.gis.format.WKT)
	{
		ol_format = new WKT();
	}
	else if(format === gc.gis.format.GEOJSON)
	{
		ol_format = new GeoJSON();
	}

	if(ol_format)
	{
		ol_geom = ol_format.readGeometry(geometry, ol_format_options);

		if(!ol_geom)
		{
			throw new gc.gis.error.GCError('Failed to parse geometry: '+ geometry);
		}
	}
	else
	{
		throw new gc.gis.error.GCError('Format not recognized: '+ format);
	}

	return ol_geom;
};
